import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatINR, formatApiErrorDetail } from "../lib/api";

const STATUSES = ["pending", "paid", "shipped", "delivered", "cancelled", "refunded"];
const TONES = {
    pending: "bg-beige text-inkDark",
    paid: "bg-green-50 text-green-700",
    shipped: "bg-skyblue/40 text-inkDark",
    delivered: "bg-skyblueDeep/15 text-skyblueDeep",
    cancelled: "bg-red-50 text-red-700",
    refunded: "bg-inkDark/5 text-inkMuted",
};

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [filter, setFilter] = useState("");
    const [active, setActive] = useState(null);
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        const params = filter ? { status: filter } : {};
        const { data } = await api.get("/admin/orders", { params });
        setOrders(data || []);
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

    const setStatus = async (orderId, fulfillmentStatus) => {
        setBusy(true);
        try {
            const { data } = await api.patch(`/admin/orders/${orderId}`, { fulfillmentStatus });
            setActive(data);
            await refresh();
            toast.success(`Status set to ${fulfillmentStatus}`);
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not update");
        } finally { setBusy(false); }
    };

    const refund = async (orderId) => {
        if (!window.confirm("Issue a full Razorpay refund for this order?")) return;
        setBusy(true);
        try {
            await api.post(`/admin/orders/${orderId}/refund`);
            toast.success("Refund initiated");
            await refresh();
            setActive(null);
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Refund failed");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="admin-orders-page">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <span className="label-eyebrow">Commerce</span>
                    <h1 className="mt-2 font-display text-4xl text-inkDark">Orders</h1>
                    <p className="mt-1 text-inkMuted">{orders.length} {orders.length === 1 ? "order" : "orders"}.</p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="label-eyebrow">Filter</label>
                    <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-full bg-ivory border border-inkDark/10 px-4 py-2 text-sm" data-testid="admin-orders-filter">
                        <option value="">All</option>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
            </div>

            <div className="mt-8 bg-ivory rounded-2xl border border-inkDark/5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-orders-table">
                        <thead className="bg-beige text-inkMuted">
                            <tr>
                                <th className="text-left px-4 py-3 font-medium">Order</th>
                                <th className="text-left px-4 py-3 font-medium">Customer</th>
                                <th className="text-left px-4 py-3 font-medium">Total</th>
                                <th className="text-left px-4 py-3 font-medium">Payment</th>
                                <th className="text-left px-4 py-3 font-medium">Fulfillment</th>
                                <th className="text-left px-4 py-3 font-medium">Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => (
                                <tr key={o.id} className="border-t border-inkDark/5 hover:bg-beige/40 cursor-pointer" onClick={() => setActive(o)} data-testid={`admin-order-row-${o.id}`}>
                                    <td className="px-4 py-3 font-mono text-xs text-inkDark">{o.id.slice(0, 8)}</td>
                                    <td className="px-4 py-3 text-inkDark">{o.userEmail}</td>
                                    <td className="px-4 py-3 text-inkDark">{formatINR(o.grandTotal)}</td>
                                    <td className="px-4 py-3"><span className={`text-[10px] px-2 py-1 rounded-full capitalize ${TONES[o.paymentStatus] || TONES.pending}`}>{o.paymentStatus}</span></td>
                                    <td className="px-4 py-3"><span className={`text-[10px] px-2 py-1 rounded-full capitalize ${TONES[o.fulfillmentStatus] || TONES.pending}`}>{o.fulfillmentStatus}</span></td>
                                    <td className="px-4 py-3 text-inkMuted">{new Date(o.createdAt).toLocaleDateString()}</td>
                                </tr>
                            ))}
                            {orders.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-10 text-center text-inkMuted">No orders yet.</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>

            {active ? (
                <div className="fixed inset-0 z-50 bg-inkDark/40 flex items-stretch justify-end" onClick={() => setActive(null)} data-testid="admin-order-drawer">
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-ivory h-full overflow-y-auto">
                        <div className="p-6 border-b border-inkDark/5 flex items-start justify-between">
                            <div>
                                <span className="label-eyebrow">Order #{active.id.slice(0, 8)}</span>
                                <h2 className="font-display text-2xl mt-1">{formatINR(active.grandTotal)}</h2>
                                <div className="text-sm text-inkMuted">{active.userEmail}</div>
                            </div>
                            <button onClick={() => setActive(null)} className="text-inkMuted">Close</button>
                        </div>
                        <div className="p-6 space-y-5">
                            <div>
                                <div className="label-eyebrow mb-2">Items</div>
                                <ul className="divide-y divide-inkDark/5">
                                    {active.items.map((it, idx) => (
                                        <li key={idx} className="py-3 flex gap-3">
                                            <img src={it.image} alt="" className="w-12 h-16 rounded-md object-cover" />
                                            <div className="flex-1">
                                                <div className="text-inkDark text-sm">{it.name}</div>
                                                <div className="text-xs text-inkMuted">Qty {it.qty}{it.size ? ` · Size ${it.size}` : ""}</div>
                                            </div>
                                            <div className="text-sm">{formatINR(it.price * it.qty)}</div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <div className="label-eyebrow mb-2">Shipping</div>
                                <address className="not-italic text-sm text-inkDark">
                                    {active.shippingAddress.name}<br />
                                    <span className="text-inkMuted">{active.shippingAddress.phone} · {active.shippingAddress.email}</span><br />
                                    {active.shippingAddress.line1}{active.shippingAddress.line2 ? `, ${active.shippingAddress.line2}` : ""}<br />
                                    {active.shippingAddress.city}, {active.shippingAddress.state} {active.shippingAddress.pincode}<br />
                                    {active.shippingAddress.country}
                                </address>
                            </div>
                            <div>
                                <div className="label-eyebrow mb-2">Update fulfillment</div>
                                <div className="flex flex-wrap gap-2">
                                    {STATUSES.map((s) => (
                                        <button
                                            key={s}
                                            disabled={busy || active.fulfillmentStatus === s}
                                            onClick={() => setStatus(active.id, s)}
                                            className={`px-3 py-1.5 rounded-full text-xs border ${active.fulfillmentStatus === s ? "bg-skyblue border-skyblue text-inkDark" : "bg-ivory border-inkDark/10 text-inkDark hover:border-skyblueDeep"} disabled:opacity-50`}
                                            data-testid={`admin-order-status-${s}`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {active.paymentStatus === "paid" ? (
                                <button onClick={() => refund(active.id)} disabled={busy} className="w-full rounded-full px-4 py-3 text-sm bg-red-50 text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50" data-testid="admin-order-refund-button">
                                    Refund full amount
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
